import _ from 'underscore';
import { DeterministicView, helpers } from '@bingads-webui/deterministic-view';
import { keyCode } from '@bingads-webui/keycode';
import template from './template.pug';

/** The DeterministicView class
 * @external DeterministicView
 * @see {@link http://bingadsinternal.azurewebsites.net/sharedcomponents/client/jsdoc/deterministic-view|DeterministicView view}
 * */

const debugLog = _.noop; // window.console.log.bind(window.console);

/**
 * Applies defaults to the PillControl's options object
 * @private
 * @param {PillControl~Options} options - the PillControl's options to apply the defaults to
 * @returns {PillControl~Options} a shallow copy of the passed in options object, with
 * defaults applied to the properties that were undefined
 */
function applyDefaults(options) {
  const propsDefaults = {
    autoSelect: true,
  };
  const stateDefaults = {
    items: [],
    selectedItem: undefined,
    disabledItems: [],
    topClasses: ['nav', 'nav-pills'],
    itemClasses: [],
  };

  const props = _.defaults(
    {},
    _.pick(options, _.keys(propsDefaults)),
    propsDefaults
  );
  const state = _.defaults(
    {},
    _.pick(options, _.keys(stateDefaults)),
    stateDefaults
  );

  // Smart logic to auto-select the first item in the list,
  // if no selectedItem was supplied. This can be disabled, by passing
  // the option autoSelect: false.
  if (props.autoSelect &&
    state.items.length > 0 && !state.selectedItem) {
    state.selectedItem = state.items[0].id;
  }

  return { props, state };
}

/**
 * Looks for the PillControl's item in the items list by the item's ID.
 * @private
 * @param {PillControl~Item[]} items - the items array to look for the item in
 * @param {any} id - the ID of the item to look for
 * @returns {(PillControl~Item|undefined)} - return the PillControl's item from the list, if found.
 * Otherwise, returns undefined.
 */
function findItem(items, id) {
  return _.find(items, item => item.id === id);
}

const idPredicate = id => item => item.id === id;
const findItemIndexById = (items, id) => _.findIndex(items, idPredicate(id));

/**
 * Commands the browser to focus the item corresponding
 * to the supplied id.
 * @this PillControl
 * @private
 * @param {any} selectedItem - the event data
 * @returns {void}
 */
function focusItem(selectedItem) {
  this.$(`a[data-id='${selectedItem}']`).focus();
}

/* eslint-disable max-len */
/**
 * Finds the item next to the currently focused or selected item.
 * @this PillControl
 * @private
 * @returns {{newIndex: Number, newItem: PillControl~Item, currentIndex: Number, currentItem: PillControl~Item}} -
 *  a record containing information about the next and the current items
 */
/* eslint-enable max-len */
function nextItem() {
  const { items, selectedItem, focusedItem } = this.model.attributes;
  const currentItem = focusedItem || selectedItem;
  const currentIndex = findItemIndexById(items, currentItem);
  const newIndex = Math.min(items.length - 1, currentIndex + 1);
  const newItem = items[newIndex];
  return {
    newIndex, newItem, currentIndex, currentItem,
  };
}

/* eslint-disable max-len */
/**
 * Finds the item previous to the currently focused or selected item.
 * @this PillControl
 * @private
 * @returns {{newIndex: Number, newItem: PillControl~Item, currentIndex: Number, currentItem: PillControl~Item}} -
 *  a record containing information about the previous and the current items
 */
/* eslint-enable max-len */
function previousItem() {
  const { items, selectedItem, focusedItem } = this.model.attributes;
  const currentItem = focusedItem || selectedItem;
  const currentIndex = findItemIndexById(items, currentItem);
  const newIndex = Math.max(0, currentIndex - 1);
  const newItem = items[newIndex];
  return {
    newIndex, newItem, currentIndex, currentItem,
  };
}

/**
 * Focuses the item next to the currently focused or selected item.
 * @this PillControl
 * @private
 * @returns {void}
 */
function focusNextItem() {
  const {
    newIndex, newItem, currentIndex, currentItem,
  } = nextItem.call(this);
  debugLog('PillControl.focusNextItem', newItem.id, newIndex, currentItem, currentIndex);
  focusItem.call(this, newItem.id);
}

/**
 * Focuses the item previous to the currently focused or selected item.
 * @this PillControl
 * @private
 * @returns {void}
 */
function focusPreviousItem() {
  const {
    newIndex, newItem, currentIndex, currentItem,
  } = previousItem.call(this);
  debugLog('PillControl.focusPreviousItem', newItem.id, newIndex, currentItem, currentIndex);
  focusItem.call(this, newItem.id);
}

/**
 * Handles click event for the pill-control items
 * Marks the item as selected if item is not disabled.
 * Changing selected item will indirectly trigger {@link PillControl#select}.
 * @this PillControl
 * @private
 * @param {MouseEvent} event - the event data
 * @returns {void}
 */
function itemClicked(event) {
  debugLog('PillControl.itemClicked', event);
  if (event.altKey || event.ctrlKey || event.shiftKey) {
    // do nothing
    return true;
  }

  const selectedItem = event.currentTarget.dataset.id;
  if (this.isItemDisabled(selectedItem)) {
    // do nothing
    return true;
  }

  helpers.updateView(this, { selectedItem });
  return false;
}

/**
 * Handles keydown event for the pill-control items.
 * The Left- and Right-arrow keys move the focus to the neighboring item.
 * The Enter key selects the currently focused item, if it is not disabled.
 * Changing selected item will indirectly trigger {@link PillControl#select}.
 * @this PillControl
 * @private
 * @param {KeyboardEvent} event - the event data
 * @returns {void}
 */
function itemPressed(event) {
  debugLog('PillControl.itemPressed', event);
  if (event.keyCode === keyCode.ArrowLeft) {
    focusPreviousItem.call(this);
  } else if (event.keyCode === keyCode.ArrowRight) {
    focusNextItem.call(this);
  } if (event.keyCode === keyCode.Enter) {
    const selectedItem = event.currentTarget.dataset.id;
    if (this.isItemDisabled(selectedItem)) {
      // do nothing
      return true;
    }
    helpers.updateView(this, { selectedItem });
  } else {
    // do nothing
    return true;
  }

  event.preventDefault();
  event.stopPropagation();

  return false;
}

/**
 * Handles focus event for the pill-control items.
 * Tracks the currently focused item, by updating the model.
 * @this PillControl
 * @private
 * @param {FocusEvent} event - the event data
 * @returns {void}
 */
function itemFocused(event) {
  const focusedItem = event.currentTarget.dataset.id;
  const newState = { focusedItem };
  debugLog('PillControl.itemFocused', focusedItem, event);
  if (!this.isItemDisabled(focusedItem)) {
    _.extend(newState, { selectedItem: focusedItem });
  }
  helpers.updateView(this, newState);
}

/**
 * Handles blur event for the pill-control items.
 * Tracks the currently focused item, by updating the model.
 * Will reset the focusedItem to null if focus left the last
 * know focused item. This will lead to changes to items tabIndex
 * attribute and will set the active or first item with tabIndex="0".
 * @this PillControl
 * @private
 * @param {FocusEvent} event - the event data
 * @returns {void}
 */
function itemBlurred(event) {
  const blurredItem = event.currentTarget.dataset.id;
  const { focusedItem } = this.model.attributes;
  debugLog('PillControl.itemBlurred', blurredItem, focusedItem, event);
  if (focusedItem === blurredItem) {
    debugLog('PillControl.itemBlurred: reset focusedItem', event, blurredItem);
    // FIXME: With DeterministicView this creates infinit loop
    // helpers.updateView(this, { focusedItem: null });
  }
}

/**
 * Handles changes to the `selectedItem` attribute of the {@link PillControl#model}.
 * @this PillControl
 * @private
 * @param {Object} model - the model. See {@link PillControl#model}.
 * @param {any} newSelection - the id of the selected item.
 * @fires PillControl#select
 * @returns {void}
 */
function onSelectionChange(model, newSelection) {
  /**
   * Select event.
   *
   * @event PillControl#select
   * @type {Object}
   * @property {PillControl~Item} item - the selected item.
   */
  this.trigger('select', {
    item: findItem(model.attributes.items, newSelection),
  });
}

/**
 * The interface of a PillControl's item object
 * @typedef {Object} PillControl~Item
 * @property {any} id - indicate the identifier of this item
 * @property {String} text - indicate the display text of this item
 */

/**
 * The interface of a PillControl's options object
 * @typedef {Object} PillControl~Options
 * @property {Boolean} [autoSelect=true] - the flag specifying whether to pre-select
 * the first item in the list, if no selectedItem was supplied
 * @property {PillControl~Item[]} [items=[]] - items to show in the pill control
 * @property {any} [selectedItem] - the id of the selected item
 * @property {Array<any>} [disabledItems=[]] - the list of ids of the disabled items
 * @property {Array<String>} [topClasses=['nav', 'nav-pills']] - the list of classes to attach
 * to the `ul` element of the default template
 * @property {Array<String>} [itemClasses=[]] - the list of classes to attach
 * to the `li` elements of the default template
 */

/**
 * The Pill Control view
 * @extends external:DeterministicView
 * @param {PillControl~Options} [userOptions] - the user options
 * @fires PillControl#select
 * @tutorial default
 */
export class PillControl extends DeterministicView {
  get className() {
    return 'pill-control';
  }

  get events() {
    return {
      'click a': itemClicked,
      'keydown a': itemPressed,
      'focus a': itemFocused,
      'blur a': itemBlurred,
    };
  }

  get template() {
    return template;
  }

  /**
   * The Pill Control view initialize method
   * @private
   * @param {PillControl~Options} [userOptions] - the user options.
   * See {@link Selector#constructor} for more details
   * @returns {void} void
   */
  initialize(userOptions) {
    const { state, options } = applyDefaults(userOptions);

    this.options = options;

    this.setState(state);

    this.model.on('change:selectedItem', onSelectionChange, this);
  }

  isItemDisabled(id) {
    const { disabledItems } = this.model.attributes;
    return disabledItems.indexOf(id) !== -1;
  }
}
